/* MolCellQuest — ホーム画面への追加（PWA インストール）
   ──────────────────────────────────────────────────────────
   毎日開いてもらうにはホーム画面にアイコンがあるのが一番効く。
   ただし出し方を間違えると邪魔なだけなので、次を守る：
     ・すでにホーム画面から起動している人には出さない
     ・初回訪問では出さない（何問か解いて価値が伝わってから）
     ・「あとで」を押されたら1週間は黙る
   この設定は端末ごとの話なので、Firestore に同期される progress
   ではなく localStorage の別キーに置く。
   ────────────────────────────────────────────────────────── */

const KEY = "molcellquest_install_v1";
const SNOOZE_DAYS = 7;

/** 何問か解いて「続けられそう」と思ってもらえた頃に出す */
export const INSTALL_MIN_SOLVED = 3;

export const install = $state({
  visible: false,
  platform: "other", // "android"（beforeinstallprompt が使える） | "ios" | "other"
  standalone: false,
  canPrompt: false, // ブラウザ標準のインストールダイアログを呼べるか
});

/** beforeinstallprompt は1度しか飛んでこないので捕まえて持っておく */
let deferredPrompt = null;

function readPrefs() {
  try {
    return JSON.parse(localStorage.getItem(KEY) || "{}") || {};
  } catch (e) {
    return {};
  }
}

function writePrefs(p) {
  try {
    localStorage.setItem(KEY, JSON.stringify(p));
  } catch (e) {
    /* プライベートブラウズなどでは黙って諦める */
  }
}

function isStandalone() {
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

function detectPlatform() {
  const ua = navigator.userAgent;
  // iPadOS 13+ は Mac を名乗るので、タッチの有無で見分ける
  const iOS =
    /iPhone|iPad|iPod/.test(ua) ||
    (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1);
  if (iOS) return "ios";
  if (/Android/.test(ua)) return "android";
  return "other";
}

export function initInstall() {
  install.standalone = isStandalone();
  install.platform = detectPlatform();

  window.addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault(); // ブラウザ独自のバナーは止めて、こちらのタイミングで出す
    deferredPrompt = e;
    install.canPrompt = true;
  });

  window.addEventListener("appinstalled", () => {
    install.visible = false;
    install.standalone = true;
    writePrefs({ ...readPrefs(), installed: true });
  });
}

/** いま出してよいか。solvedCount は呼び出し側から渡す（循環 import を避ける） */
export function shouldShowInstall(solvedCount) {
  if (install.standalone || isStandalone()) return false;
  if (solvedCount < INSTALL_MIN_SOLVED) return false;

  const p = readPrefs();
  if (p.installed || p.never) return false;
  if (p.snoozedAt && Date.now() - p.snoozedAt < SNOOZE_DAYS * 86400000) return false;

  // iOS は手順を案内するしかない。それ以外はダイアログを呼べるときだけ。
  return install.platform === "ios" || install.canPrompt;
}

export function openInstall() {
  install.visible = true;
}

/** 「あとで」。1週間は出さない。 */
export function snoozeInstall() {
  install.visible = false;
  writePrefs({ ...readPrefs(), snoozedAt: Date.now() });
}

/** 「追加しない」。以降は自分から開かない限り出さない。 */
export function neverInstall() {
  install.visible = false;
  writePrefs({ ...readPrefs(), never: true });
}

/**
 * ブラウザ標準のインストールダイアログを出す（Android / デスクトップ Chrome）。
 * @returns {Promise<"accepted"|"dismissed"|"unavailable">}
 */
export async function promptInstall() {
  if (!deferredPrompt) return "unavailable";
  const e = deferredPrompt;
  deferredPrompt = null;
  install.canPrompt = false;
  try {
    e.prompt();
    const { outcome } = await e.userChoice;
    if (outcome === "accepted") writePrefs({ ...readPrefs(), installed: true });
    else writePrefs({ ...readPrefs(), snoozedAt: Date.now() });
    install.visible = false;
    return outcome;
  } catch (err) {
    install.visible = false;
    return "unavailable";
  }
}

/** Service Worker の登録。失敗してもアプリは普通に動く。 */
export function registerServiceWorker() {
  if (!("serviceWorker" in navigator)) return;
  if (location.protocol !== "https:" && location.hostname !== "localhost") return;
  window.addEventListener("load", () => {
    navigator.serviceWorker.register("./sw.js").catch((e) => {
      console.warn("[MolCellQuest] Service Worker を登録できませんでした", e);
    });
  });
}
